import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import PageBreadcrumb from '@/components/common/PageBreadCrumb';
import PageMeta from '@/components/common/PageMeta';
import { useRenting, useCancelRenting, useConfirmRenting, useUpdateRenting } from '@/features/rentings/hooks/useRentings';
import Badge from '@/components/ui/badge/Badge';
import Button from '@/components/ui/button/Button';
import ImagePreview from '@/components/ui/images/ImagePreview';
import { Modal } from '@/components/ui/modal';
import { useModal } from '@/shared/hooks/useModal';
import { formatDate } from '@fullcalendar/core/index.js';
import CarReportsList from '@/features/cars/components/CarReportsList';
import { useQueryClient } from '@tanstack/react-query';
import RentingCommentForm from '@/features/rentings/components/RentingCommentForm';
import RentingEditForm from '@/features/rentings/components/RentingEditForm';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/toast/Toast';
import DetailsCard from '@/components/common/DetailsCard';

function formatPrice(price: string | number, currency: string): string {
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(numericPrice);
}

const RentingDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: renting, isLoading, error } = useRenting(id!);
  const cancelRenting = useCancelRenting();
  const confirmRenting = useConfirmRenting();
  const updateRenting = useUpdateRenting();
  const { isOpen: isCancelOpen, openModal: openCancelModal, closeModal: closeCancelModal } = useModal();
  const { isOpen: isConfirmOpen, openModal: openConfirmModal, closeModal: closeConfirmModal } = useModal();
  const { isOpen: isCommentOpen, openModal: openCommentModal, closeModal: originalCloseCommentModal } = useModal();
  const { isOpen: isEditOpen, openModal: openEditModal, closeModal: originalCloseEditModal } = useModal();

  const commentFormRef = useRef<{ handleClose: () => void; isDirty: () => boolean } | null>(null);
  const rentingEditFormRef = useRef<{ handleClose: () => void; isDirty: () => boolean } | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const { t } = useTranslation();
  const toast = useToast();

  // Reset updating state when renting ID changes
  useEffect(() => {
    setIsUpdating(false);
  }, [id]);


  const closeCommentModal = useCallback(() => {
    if (commentFormRef.current?.isDirty?.()) {
      commentFormRef.current.handleClose();
    } else {
      originalCloseCommentModal();
    }
  }, [originalCloseCommentModal]);

  const closeEditModal = useCallback(() => {
    if (rentingEditFormRef.current?.isDirty?.()) {
      rentingEditFormRef.current.handleClose();
    } else {
      originalCloseEditModal();
    }
  }, [originalCloseEditModal]);

  const handleCancelClick = () => {
    openCancelModal();
  };

  const handleConfirmClick = () => {
    openConfirmModal();
  };

  // Check if renting has already started
  const isRentingStarted = () => {
    if (!renting) return false;

    const startDateTime = new Date(`${renting.start_date}T${renting.start_time}`);
    const now = new Date();

    return startDateTime <= now;
  };

  const handleEditClick = () => {
    openEditModal();
  };

  const handleConfirmCancel = async () => {
    try {
      await cancelRenting.mutateAsync(id!);
      closeCancelModal();
      toast.success(t('rentings.cancel.success'));
    } catch (error) {
      toast.error(t('rentings.cancel.error') + `<br/><br/>${error}`);
    }
  };

  const handleConfirmRenting = async () => {
    try {
      await confirmRenting.mutateAsync(id!);
      closeConfirmModal();
      toast.success(t('rentings.confirm.success'));
    } catch (error) {
      toast.error(t('rentings.confirm.error') + `<br/><br/>${error}`);
    }
  };

  const handleEditSubmit = async (data: any) => {
    try {
      setIsUpdating(true);
      const result = await updateRenting.mutateAsync({ rentingId: id!, data });
      originalCloseEditModal();

      // Invalidate cache for the new renting
      queryClient.invalidateQueries({ queryKey: ['rentings', result.id] });

      // Reset updating state before navigation
      setIsUpdating(false);

      // Wait a bit for the server to fully create the new renting before navigating
      setTimeout(() => {
        navigate(`/rentings/${result.id}`);
      }, 1500);
    } catch (error: any) {
      setIsUpdating(false);
      console.error('Failed to update renting:', error);

      // Handle specific API errors
      if (error.message) {
        const errorMessage = error.message.toLowerCase();

        if (errorMessage.includes('object_not_found')) {
          alert(t('rentings.errors.not_found'));
        } else if (errorMessage.includes('renting_locked')) {
          alert(t('rentings.errors.locked'));
        } else if (errorMessage.includes('renting_is_not_actual')) {
          alert(t('rentings.errors.not_actual'));
        } else if (errorMessage.includes('car_not_available')) {
          alert(t('rentings.errors.car_not_available'));
        } else if (errorMessage.includes('cannot_change_renting_options')) {
          alert(t('rentings.errors.renting_started_cannot_edit'));
        } else if (errorMessage.includes('extra_service_not_found')) {
          alert(t('rentings.errors.extra_service_not_found'));
        } else if (errorMessage.includes('extra_service_max_amount_exceeded')) {
          alert(t('rentings.errors.extra_service_max_exceeded'));
        } else {
          alert(t('rentings.errors.update_failed'));
        }
      } else {
        alert(t('rentings.errors.update_failed'));
      }
    }
  };

  if (isLoading) {
    return <div>{t("Loading...")}</div>;
  }

  if (isUpdating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-text-secondary">
            {t('Updating rental and creating new version...')}
          </p>
        </div>
      </div>
    );
  }

  if (error || !renting) {
    return <div>{t("Error")}: {error?.message}</div>;
  }

  const getStatusBadgeProps = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return { color: 'success' as const, variant: 'light' as const };
      case 'draft':
        return { color: 'light' as const, variant: 'light' as const };
      case 'cancelled':
      case 'cancel':
        return { color: 'error' as const, variant: 'light' as const };
      case 'pending':
        return { color: 'warning' as const, variant: 'light' as const };
      default:
        return { color: 'light' as const, variant: 'light' as const };
    }
  };

  const pageName = `${t("rentings.breadcrumb.renting")} ${renting.car.registration_number} (${formatDate(renting.start_date + ' ' + renting.start_time, { month: 'long', day: 'numeric', year: 'numeric' })})`;
  const hasExtraServices = renting.extra_services.length > 0;
  const hasComments = renting.comments.length > 0;

  return (
    <>
      <PageMeta
        title={`${t("rentings.breadcrumb.renting")} #${renting.number} | Admin.Drivitive`}
        description={t("Renting details")}
      />
      <PageBreadcrumb
        items={[
          { label: t('nav.rentings'), path: '/rentings' },
          { label: pageName }
        ]}
        actions={
          <div className="flex gap-3">
            {renting.status.toLowerCase() === 'draft' && (
              <Button
                variant="primary"
                size="md"
                onClick={handleConfirmClick}
              >
                {t("Confirm")}
              </Button>
            )}
            {renting.status.toLowerCase() !== 'cancelled' && (
              <Button
                variant="danger"
                size="md"
                onClick={handleCancelClick}
              >
                {t("Cancel")}
              </Button>
            )}
          <div title={isRentingStarted() ? t('rentings.edit.disabled_tooltip') : t('rentings.edit.tooltip')}>
            <Button
              variant="outline"
              size="md"
              onClick={handleEditClick}
              disabled={isRentingStarted()}
              className="flex items-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              {t("Edit Renting")}
            </Button>
          </div>
        </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-3 2xl:gap-7.5">
        <DetailsCard
          title={t("Rental Details")}
        >
          <div className="space-y-4">
            <div className="mt-[-8px] mb-8">
              <Badge {...getStatusBadgeProps(renting.status)} size="lg">
                {renting.status.charAt(0).toUpperCase() + renting.status.slice(1)}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">{t("Total price")}</p>
              <p className="font-medium text-text-primary">
                {formatPrice(renting.total_price, renting.currency)}
                {renting.original_price && (
                  <span className="text-sm text-gray-500 ml-2">
                    (Original: {formatPrice(renting.original_price, renting.currency)}, {renting.car.price}/day)
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">{t("Period")}</p>
              <div className="flex items-center gap-2">
                <p className="font-medium text-text-primary">
                  <span className="text-xl">{new Date(renting.start_date).toLocaleDateString()}</span> <span className="text-md font-normal text-text-tertiary">{renting.start_time}</span>
                  {' — '}
                  <span className="text-xl">{new Date(renting.end_date).toLocaleDateString()}</span> <span className="text-md font-normal text-text-tertiary">{renting.end_time}</span>
                </p>
                {['confirmed', 'draft'].includes(renting.status.toLowerCase()) && (
                  <Link
                    to={`/rent-calendar?date=${renting.start_date}&highlight=${renting.id}`}
                    className="ml-2 text-brand-500 hover:text-brand-600"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm text-text-tertiary">{t("Client")}</p>
                <p className="font-medium text-text-primary">
                  <Link
                    to={`/clients/${renting.client.id}`}
                    className="block relative w-fit hover:underline"
                    title={t("View details")}
                  >
                    {renting.client.first_name} {renting.client.last_name}
                  </Link>
                </p>
              </div>
              <div>
                <p className="text-sm text-text-tertiary">{t("Contact info")}</p>
                <a
                  href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(renting.client.email)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:underline block"
                >
                  {renting.client.email}
                </a>
                <p className="font-medium text-text-primary">{renting.client.phone}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-text-tertiary">{t("Created")}</p>
              <p className="font-medium text-text-primary">
                {new Date(renting.created_at).toLocaleString()}
              </p>
            </div>
          </div>
        </DetailsCard>

        <DetailsCard
          title={t("Delivery Details")}
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm text-text-tertiary mb-2">{t("Delivery Address")}</p>
              <p className="font-medium text-text-primary leading-[1] mb-2">{renting.address_to_deliver}</p>
              <p className="text-xs">
                <a href={`https://maps.google.com/?q=${renting.deliver_latitude},${renting.deliver_longitude}`} target="_blank" rel="noopener noreferrer" className="flex items-center text-brand-500 hover:text-brand-600">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 5.5-9 13-9 13s-9-7.5-9-13a9 9 0 1 1 18 0z" />
                    <path d="M12 14l.01 0" />
                  </svg>
                  {renting.deliver_latitude}, {renting.deliver_longitude}
                </a>
              </p>
            </div>
            <div>
              <p className="text-sm text-text-tertiary mb-2">{t("Pickup Address")}</p>
              <p className="font-medium text-text-primary leading-[1] mb-2">{renting.address_to_pickup}</p>
              <p className="text-xs">
                <a href={`https://maps.google.com/?q=${renting.pickup_latitude},${renting.pickup_longitude}`} target="_blank" rel="noopener noreferrer" className="flex items-center text-brand-500 hover:text-brand-600">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 5.5-9 13-9 13s-9-7.5-9-13a9 9 0 1 1 18 0z" />
                    <path d="M12 14l.01 0" />
                  </svg>
                  {renting.pickup_latitude}, {renting.pickup_longitude}
                </a>
              </p>
            </div>
          </div>
        </DetailsCard>

        <DetailsCard
          title={t("Car")}
        >
          <div className="space-y-4">
            <div className="relative">
              <Link
                to={`/cars/${renting.car.id}`}
                className="block relative"
                title={t("View details")}
              >
                <img
                  src={renting.car.image_url}
                  alt={`${renting.car.model.brand_name} ${renting.car.model.model_name}`}
                  className="w-full rounded-lg object-cover flex-shrink-0 border border-border-primary outline-[0px] transition-all duration-100 hover:outline hover:outline-[2px] outline-brand-400"
                />
              </Link>
            </div>
            <div className="space-y-2">
              <h3 className="font-medium text-text-primary">
                {renting.car.model.brand_name} {renting.car.model.model_name}
              </h3>
              <div className="flex gap-2">
                <Badge size="sm" color="primary">
                  {renting.car.model.body_type.toUpperCase()}
                </Badge>
                <Badge size="sm" color="info">
                  {renting.car.model.transmission_type.toUpperCase()}
                </Badge>
              </div>
              <p className="text-sm text-text-tertiary">
                {t("Year")}: {renting.car.release_year}
              </p>
              <p className="text-sm text-text-tertiary">
                {t("Registration number")}: {renting.car.registration_number}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-text-tertiary">{t("Color")}:</span>
                <div
                  className="w-6 h-6 rounded-full border border-gray-200"
                  style={{ backgroundColor: renting.car.color }}
                />
              </div>
            </div>
          </div>
        </DetailsCard>

        <DetailsCard
          title={t("Extra Services")}
        >
          <div className="space-y-4">
            {hasExtraServices ? renting.extra_services.map(service => (
              <div key={service.id} className="border-b border-border-primary pb-4 last:border-0 last:pb-0">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-text-primary">{service.name}</p>
                    {service.description && (
                      <p className="text-sm text-text-tertiary">{service.description}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-text-primary">
                      {formatPrice(service.price, service.currency)}
                    </p>
                    <p className="text-sm text-gray-500">× {service.amount}</p>
                  </div>
                </div>
              </div>
            )) : (
              <p className="text-sm text-text-tertiary">{t("No extra services")}</p>
            )}
          </div>
        </DetailsCard>

        <CarReportsList
          rentingId={id!}
          carId={renting.car.id}
          rentingStatus={renting.status.toLowerCase() as 'draft' | 'confirmed' | 'cancelled'}
        />

        <div className="rounded-lg border border-border-primary bg-bg-card p-4 md:p-6 xl:p-7.5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-text-primary">{t("Comments")}</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={openCommentModal}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{t("Add comment")}</span>
            </Button>
          </div>
          <div className="space-y-4">
            {hasComments ? renting.comments.map(comment => (
              <div key={comment.id} className="border-b border-border-primary pb-4 last:border-0 last:pb-0">
                <div className="flex justify-between items-start">
                  <div className="flex-1 mr-4">
                    <p className="text-sm text-text-primary">{comment.text}</p>
                    {comment.file_url && (
                      <div className="mt-3 max-w-[150px]">
                        <ImagePreview imageUrl={comment.file_url}>
                          <img
                            src={comment.file_url}
                            alt={t("Comment attachment")}
                            className="rounded-lg border border-border-primary hover:border-primary transition-colors"
                          />
                        </ImagePreview>
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm text-text-tertiary">
                      {new Date(comment.created_at).toLocaleString()}
                    </p>
                    <p className="text-sm text-text-tertiary">
                      {comment.employee.email}
                    </p>
                  </div>
                </div>
              </div>
            )) : (
              <p className="text-sm text-text-tertiary">{t("No comments")}</p>
            )}
          </div>
        </div>

        <Modal
          isOpen={isConfirmOpen}
          onClose={closeConfirmModal}
          className="max-w-[400px] p-6"
        >
          <div className="text-center">
            <h3 className="mb-4 text-xl font-semibold text-text-primary">
              {t("Confirm Renting")}
            </h3>
            <img
              src={renting.car.image_url}
              alt={`${renting.car.model.brand_name} ${renting.car.model.model_name}`}
              className="w-full max-w-[250px] mx-auto my-8 rounded-lg object-cover flex-shrink-0 border border-gray-200"
            />
            <p className="mb-6 text-text-tertiary">
              {t("Are you sure you want to confirm this renting?")}
            </p>
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={closeConfirmModal}
              >
                {t("No, cancel")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirmRenting}
                disabled={confirmRenting.isPending}
              >
                {confirmRenting.isPending ? t("Confirming...") : t("Yes, confirm it")}
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={isCancelOpen}
          onClose={closeCancelModal}
          className="max-w-[400px] p-6"
        >
          <div className="text-center">
            <h3 className="mb-4 text-xl font-semibold text-text-primary">
              {t("Cancel Renting")}
            </h3>
            <img
              src={renting.car.image_url}
              alt={`${renting.car.model.brand_name} ${renting.car.model.model_name}`}
              className="w-full max-w-[250px] mx-auto my-8 rounded-lg object-cover flex-shrink-0 border border-gray-200"
            />
            <p className="mb-6 text-text-tertiary">
              {t("Are you sure you want to cancel this renting?")}
              <br />
              {t("This action cannot be undone.")}
            </p>
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={closeCancelModal}
              >
                {t("No, keep it")}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleConfirmCancel}
                disabled={cancelRenting.isPending}
              >
                {cancelRenting.isPending ? t("Canceling...") : t("Yes, cancel it")}
              </Button>
            </div>
          </div>
        </Modal>


        <Modal
          isOpen={isCommentOpen}
          onClose={closeCommentModal}
          className="max-w-[600px] p-6"
        >
          <RentingCommentForm
            ref={commentFormRef}
            rentingId={id!}
            onSubmit={() => {
              queryClient.invalidateQueries({ queryKey: ['rentings', id] });
              originalCloseCommentModal();
            }}
            onClose={originalCloseCommentModal}
          />
        </Modal>

        <Modal
          isOpen={isEditOpen}
          onClose={closeEditModal}
          maxWidth="800px"
          className="p-6 lg:p-10"
        >
          <RentingEditForm
            ref={rentingEditFormRef}
            rentingId={id!}
            client={renting.client}
            car={renting.car}
            initialData={{
              start_date: renting.start_date,
              start_time: renting.start_time,
              end_date: renting.end_date,
              end_time: renting.end_time,
              address_to_deliver: renting.address_to_deliver,
              address_to_pickup: renting.address_to_pickup,
              deliver_latitude: renting.deliver_latitude || 0,
              deliver_longitude: renting.deliver_longitude || 0,
              pickup_latitude: renting.pickup_latitude || 0,
              pickup_longitude: renting.pickup_longitude || 0,
              extra_services: renting.extra_services?.map(service => ({
                id: service.id,
                amount: service.amount || 1
              })) || [],
            }}
            onSubmit={handleEditSubmit}
            onClose={originalCloseEditModal}
          />
        </Modal>
      </div>
    </>
  );
};

export default RentingDetailsPage;
